import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import paper from 'paper/dist/paper-full.min';
import chalkTexturePath from 'assets/OldWDT/photo/draw-stage/chalk-texture2greyLight.png';
import chalkPointerPath from 'assets/OldWDT//photo/draw-stage/crayon-pointer.png';
import smileyPointerPath from 'assets/OldWDT//photo/draw-stage/Smiley_200x200.png';
import arrowPointerPath from 'assets/OldWDT//photo/draw-stage/Arrow_200x200.png';
import { resizeCanvas } from 'utils/managers/canvas-manager';
import { scaleSVG } from 'utils/managers/svg-manager';
import guidelineManager from 'utils/managers/guideline-manager';
import textureManager from 'utils/managers/texture-manager';

import { getLocale } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { t } from '../../translations';

export class DrawActivityTutorial extends PureComponent {
  constructor(props) {
    super(props);

    this._canvas = null;
    this.timers = [];
    this.chalkPointer = null;
    this.waitedForPointer = false;

    this.handbrake = null;
    this.pathsDrawn = 0;
    this.drawConfig = {
      density: 10,
      timeInterval: 50
    };
    this.instructionPlaying = false;
    this.guideLineIndex = 0;
    this.firstCharacterDrawn = false;
    this.cursorMovedToNextNumber = false;
    this.handleSVGLoaded = this.handleSVGLoaded.bind(this);
    this.handleStartIntro = this.handleStartIntro.bind(this);
    this.currentAudioInstruction = null;

    this.state = {
      helpers: {
        arrow: {
          show: false,
          x: 0,
          y: 0,
          width: 0,
          height: 0
        },
        smiley: {
          show: false,
          x: 0,
          y: 0,
          width: 0,
          height: 0
        }
      }
    };
  }

  async componentDidMount() {
    let scaleProportion = 1;
    const iPadWidth = 1536 / 2;
    let currentWidth = this._container.getBoundingClientRect().width;
    let originalDrawConfig = this.drawConfig;

    scaleProportion = currentWidth / iPadWidth;
    this.drawConfig = {
      density: originalDrawConfig.density * scaleProportion,
      timeInterval: originalDrawConfig.timeInterval
    };
    paper.setup(this._canvas);
    this.characterSvgPath = this.props.svgSrc;
    paper.project.importSVG(this.characterSvgPath, { onLoad: this.handleSVGLoaded });
  }

  componentWillUnmount() {
    const {
      audio,
      data: { crayonDrawing }
    } = this.props.currentRound;

    if (this.props.level === 1) {
      this.props.inactivityManager && this.props.inactivityManager.clear();
    }
    this.currentAudioInstruction && this.currentAudioInstruction.stop();
    audio[crayonDrawing.src] && audio[crayonDrawing.src].stop();
    paper.project.clear();
    paper.project.remove();
    const context = this._canvas.getContext('2d');
    context.clearRect(0, 0, this._canvas.width, this._canvas.height);
    this._canvas.remove();
    if (this.timers.length > 0) {
      this.timers.forEach(el => {
        clearTimeout(el);
      });
    }

    if (this.handbrake) {
      clearInterval(this.handbrake);
    }
  }

  handleSVGLoaded(characterSvg) {
    const {
      state: {
        helpers: { arrow, smiley }
      }
    } = this;

    if (this._canvas && this._container) {
      scaleSVG(characterSvg, this._canvas, false);
      resizeCanvas(this._canvas, this._container);
      guidelineManager.getGuidelines(this.props.segments, paper.project, this.drawConfig.density);
      guidelineManager.setCurrentGuideline(this.guideLineIndex);

      this.chalkPointer = this.initializeChalkPointer(guidelineManager.currentGuideline);

      if (['8', '9'].includes(this.props.character)) {
        this.setHelperArrow(guidelineManager.currentGuideline, true);
      } else {
        this.setHelperSmiley(guidelineManager.currentGuideline, true);
      }

      if (this.props.level === 1) {
        if (this.props.character.length === 1) {
          const oneStarOneCharIntroInterval = setTimeout(() => {
            if (this.props.level >= 2) {
              this.drawCharacter();
              this.setState(state => ({
                ...state,
                helpers: {
                  arrow: {
                    ...arrow,
                    show: false
                  },
                  smiley: {
                    ...smiley,
                    show: false
                  }
                }
              }));
            }
          }, 2500);
          this.timers.push(oneStarOneCharIntroInterval);
        } else {
          const oneStarTwoCharIntroInterval = setTimeout(() => {
            if (this.props.level >= 2) {
              this.drawCharacter();
              this.setState(state => ({
                ...state,
                helpers: {
                  arrow: {
                    ...arrow,
                    show: false
                  },
                  smiley: {
                    ...smiley,
                    show: false
                  }
                }
              }));
            }
          }, 1000);
          this.timers.push(oneStarTwoCharIntroInterval);
        }
      } else {
        const moreStarIntroInterval = setTimeout(() => {
          if (this.props.level >= 2) {
            this.drawCharacter();
            this.setState(state => ({
              ...state,
              helpers: {
                arrow: {
                  ...arrow,
                  show: false
                },
                smiley: {
                  ...smiley,
                  show: false
                }
              }
            }));
          }
        }, 1000);
        this.timers.push(moreStarIntroInterval);
      }
      textureManager.initializeTexture('chalk-texture', paper, this._canvas);
    }
  }

  drawCharacter() {
    const locale = getLocale();

    const {
      state: {
        helpers: { arrow, smiley }
      }
    } = this;

    this.setChalkPointOpacity(guidelineManager.currentGuideline.isTransportPath);

    if (guidelineManager.currentGuideline.isLine) {
      if (this.props.level >= 2 && this.props.character === '20' && this.pathsDrawn === 3) {
        this.chalkPointer.opacity = 0;
        const pointerTimer = setTimeout(
          () => {
            if (arrow.show) {
              this.setState(state => ({
                ...state,
                helpers: {
                  ...state.helpers,
                  arrow: {
                    ...arrow,
                    show: false
                  }
                }
              }));
              this.chalkPointer.opacity = 1;
            } else if (smiley.show) {
              this.setState(state => ({
                ...state,
                helpers: {
                  ...state.helpers,
                  smiley: {
                    ...smiley,
                    show: false
                  }
                }
              }));
              this.chalkPointer.opacity = 1;
            }
            this.waitedForPointer = true;
            this.drawPath(this.drawConfig.timeInterval).then(() => {
              this.advanceCharacterDraw();
            });
          },
          locale === 'en-US' ? 4200 : 7200
        );
        this.timers.push(pointerTimer);
      } else if (this.props.level >= 2 && this.cursorMovedToNextNumber && !this.waitedForPointer) {
        this.chalkPointer.opacity = 0;
        const secondPointerTimer = setTimeout(
          () => {
            if (arrow.show) {
              this.setState(state => ({
                ...state,
                helpers: {
                  ...state.helpers,
                  arrow: {
                    ...arrow,
                    show: false
                  }
                }
              }));
              this.chalkPointer.opacity = 1;
            } else if (smiley.show) {
              this.setState(state => ({
                ...state,
                helpers: {
                  ...state.helpers,
                  smiley: {
                    ...smiley,
                    show: false
                  }
                }
              }));
              this.chalkPointer.opacity = 1;
            }
            this.waitedForPointer = true;
            this.drawPath(this.drawConfig.timeInterval).then(() => {
              this.advanceCharacterDraw();
            });
          },
          locale === 'en-US' ? 3200 : 5800
        );
        this.timers.push(secondPointerTimer);
      } else {
        this.drawPath(this.drawConfig.timeInterval).then(() => {
          this.advanceCharacterDraw();
        });
      }
    } else {
      const { x, y } = guidelineManager.getGuidelineBounds();
      textureManager.drawDenseTexturePoint({ x, y });
      this.advanceCharacterDraw();
    }
  }

  advanceCharacterDraw() {
    const {
      state: {
        helpers: { arrow }
      }
    } = this;

    this.guideLineIndex++;
    guidelineManager.setCurrentGuideline(this.guideLineIndex);

    if (this.guideLineIndex < guidelineManager.guidelineSVGs.length) {
      if (this.props.level >= 2) {
        if (this.props.character.length > 1 && +this.props.character !== 20) {
          if (!this.cursorMovedToNextNumber && !this.firstCharacterDrawn) {
            this.firstCharacterDrawn = true;
            this.drawCharacter();
          } else if (!this.cursorMovedToNextNumber && this.firstCharacterDrawn) {
            if (['10', '18', '19'].includes(this.props.character)) {
              this.setHelperArrow(guidelineManager.currentGuideline, true);
            } else {
              this.setHelperSmiley(guidelineManager.currentGuideline, true);
            }
            this.cursorMovedToNextNumber = true;
            this.setChalkPointOpacity(false, true);
            this.drawCharacter();
          } else {
            this.drawCharacter();
          }
        } else if (this.props.character.length > 1 && +this.props.character === 20) {
          this.pathsDrawn++;
          switch (this.pathsDrawn) {
            case 1:
              this.drawCharacter();
              break;
            case 2:
              this.firstCharacterDrawn = true;
              this.drawCharacter();
              break;
            case 3:
              this.setState(state => ({
                ...state,
                helpers: {
                  ...state.helpers,
                  arrow: {
                    ...arrow,
                    show: false
                  }
                }
              }));
              this.cursorMovedToNextNumber = true;
              this.setChalkPointOpacity(false, true);
              this.drawCharacter();
              break;
            default:
              this.drawCharacter();
              break;
          }
        } else {
          this.drawCharacter();
        }
      } else {
        if (this.props.character.length > 1 && +this.props.character !== 20) {
          if (!this.cursorMovedToNextNumber && !this.firstCharacterDrawn) {
            this.firstCharacterDrawn = true;
            this.drawCharacter();
          } else if (!this.cursorMovedToNextNumber && this.firstCharacterDrawn) {
            // if (this.props.level === 1) {
            //   this.props.onNextInstruction();
            // }
            if (['10', '18', '19'].includes(this.props.character)) {
              this.setHelperArrow(guidelineManager.currentGuideline, true);
            } else {
              this.setHelperSmiley(guidelineManager.currentGuideline, true);
            }
            this.cursorMovedToNextNumber = true;
            this.setChalkPointOpacity(false, true);
          } else {
            this.drawCharacter();
          }
        } else if (this.props.character.length > 1 && +this.props.character === 20) {
          this.pathsDrawn++;

          switch (this.pathsDrawn) {
            case 1:
              this.drawCharacter();
              break;
            case 2:
              this.firstCharacterDrawn = true;
              this.drawCharacter();
              break;
            case 3:
              // if (this.props.level === 1) {
              //   this.props.onNextInstruction();
              // }
              this.setHelperArrow(guidelineManager.currentGuideline, true);
              this.cursorMovedToNextNumber = true;
              this.setChalkPointOpacity(false, true);

              break;
            default:
              this.drawCharacter();
              break;
          }
        } else {
          this.drawCharacter();
        }
      }
    } else {
      this.setChalkPointOpacity(false, true);
      if (this.props.onComplete) {
        const completeTimer = setTimeout(() => {
          this.props.onComplete();
          this.chalkPointer && this.chalkPointer.remove();
        }, 1500);
        this.timers.push(completeTimer);
      }
    }
  }

  setChalkPointOpacity(isTransport, hide) {
    if (hide) {
      this.chalkPointer.opacity = 0;
    } else {
      if (isTransport) {
        this.chalkPointer.opacity = 0.5;
      } else {
        this.chalkPointer.opacity = 1;
      }
    }
  }

  getDontWaitForSound() {
    let isNextContinuePath = false;
    let dontWaitForSound = false;
    let nextSoundWaits = false;

    if (this.guideLineIndex < guidelineManager.guidelineSVGs.length - 1) {
      isNextContinuePath = guidelineManager.guidelineSVGs[this.guideLineIndex + 1].isContinuePath;
      nextSoundWaits = guidelineManager.guidelineSVGs[this.guideLineIndex + 1].isWait;
      if (isNextContinuePath) {
        dontWaitForSound = true;
      }
      if (nextSoundWaits) {
        dontWaitForSound = false;
      }
    }

    return dontWaitForSound;
  }

  drawPath(timeInterval) {
    const {
      audio,
      data: { crayonDrawing }
    } = this.props.currentRound;

    const guideLineSVG = guidelineManager.currentGuideline;
    const length = guideLineSVG.data.coordinates.length - 1;
    let index = guideLineSVG.isReversed ? length : 0;
    let x = 0;
    let y = 0;
    let soundReady = false;
    let drawReady = false;

    let dontWaitForSound = this.getDontWaitForSound();

    if (this.props.svgSounds[guideLineSVG.lineId]) {
      this.currentAudioInstruction = this.props.svgSounds[guideLineSVG.lineId];

      this.currentAudioInstruction.once('playerror', () => {
        soundReady = true;
        dontWaitForSound = true;
      });

      this.currentAudioInstruction.once('play', () => {
        soundReady = true;
        this.instructionPlaying = true;
        if (drawReady && !dontWaitForSound) {
          clearInterval(this.handbrake);
        }
      });

      if (
        Object.keys(this.props.svgSounds).indexOf(guideLineSVG.lineId) === 0 &&
        +this.props.character > 9
      ) {
        if (+this.props.character !== 20) {
          this.currentAudioInstruction.once('end', () => {
            if (this.props.level >= 2) {
              const instructionTimer = setTimeout(() => {
                this.props.onNextInstruction();
              }, 5000);
              this.timers.push(instructionTimer);
            } else {
              const instructionsTimer = setTimeout(() => {
                this.props.onNextInstruction();
              }, 5000);
              this.timers.push(instructionsTimer);
            }
          });
        }
      } else if (
        Object.keys(this.props.svgSounds).indexOf(guideLineSVG.lineId) === 1 &&
        +this.props.character === 20
      ) {
        this.currentAudioInstruction.once('end', () => {
          if (this.props.level >= 2) {
            const thirdInstructionTimer = setTimeout(() => {
              this.props.onNextInstruction();
            }, 4000);
            this.timers.push(thirdInstructionTimer);
          } else {
            const fourthInstructionTimer = setTimeout(() => {
              this.props.onNextInstruction();
            }, 4500);
            this.timers.push(fourthInstructionTimer);
          }
        });
      }
    } else {
      soundReady = true;
      dontWaitForSound = true;
      this.instructionPlaying = false;
    }
    if (!guideLineSVG.isTransportPath) {
      audio[crayonDrawing.src].play();
    } else {
      audio[crayonDrawing.src].pause();
    }

    return new Promise(resolve => {
      if (this.props.svgSounds[guideLineSVG.lineId]) {
        setTimeout(() => this.currentAudioInstruction.play(), 200);
      }

      this.handbrake = setInterval(() => {
        if (guideLineSVG.isReversed) {
          if (index > 0) {
            ({ x, y } = guideLineSVG.data.coordinates[index]);
            if (!guideLineSVG.isTransportPath) {
              textureManager.drawTexturePoint({ x, y }, index);
            }

            this.moveChalkPointer(this.chalkPointer, x, y);

            index--;
          } else if (index === 0) {
            ({ x, y } = guideLineSVG.data.coordinates[index]);
            if (!guideLineSVG.isTransportPath) {
              textureManager.drawTexturePoint({ x, y }, index);
            }

            this.moveChalkPointer(this.chalkPointer, x, y);

            index--;
          } else {
            audio[crayonDrawing.src].pause();
            drawReady = true;
            if (soundReady || dontWaitForSound) {
              clearInterval(this.handbrake);
              resolve();
            }
          }
        } else {
          if (index <= length) {
            ({ x, y } = guideLineSVG.data.coordinates[index]);
            if (!guideLineSVG.isTransportPath) {
              textureManager.drawTexturePoint({ x, y }, index);
            }

            this.moveChalkPointer(this.chalkPointer, x, y);

            index++;
          } else {
            audio[crayonDrawing.src].pause();
            drawReady = true;
            if (soundReady || dontWaitForSound) {
              clearInterval(this.handbrake);
              resolve();
            }
          }
        }
      }, timeInterval);
    });
  }

  // addTouchEventListeners() {
  //   this._canvas.addEventListener('touchstart', this.touchHandleStartIntro);
  //   this._canvas.addEventListener('mousedown', this.handleStartIntro);
  // }

  // removeTouchEventListeners() {
  //   this._canvas.removeEventListener('touchstart', this.touchHandleStartIntro);
  //   this._canvas.removeEventListener('mousedown', this.handleStartIntro);
  // }

  touchHandleStartIntro = event => {
    if (event.targetTouches.length >= 2) {
      console.log('Found and prevented multi-touch gesture on touchstart'); // eslint-disable-line no-console
      event.preventDefault();
    } else {
      this.handleStartIntro(event);
    }
  };

  handleStartIntro() {
    const {
      props: { inactivityManager: inactivity },
      state: {
        helpers: { arrow, smiley }
      }
    } = this;

    if (inactivity) {
      inactivity.stopAudio();
      inactivity.clear();
    }

    if (smiley.show) {
      this.setState(state => ({
        ...state,
        helpers: { ...state.helpers, smiley: { ...smiley, show: false } }
      }));
    }

    if (arrow.show) {
      this.setState(state => ({
        ...state,
        helpers: { ...state.helpers, arrow: { ...arrow, show: false } }
      }));
    }

    this.setChalkPointOpacity(false, false);
    this.drawCharacter();
  }

  getScaleProportion() {
    let scaleProportion = 1;
    const iPadWidth = 1536 / 2;
    let currentWidth = this._container.getBoundingClientRect().width;

    scaleProportion = currentWidth / iPadWidth;

    return scaleProportion;
  }

  initializeChalkPointer(firstGuideLine) {
    const chalkPointer = new paper.Raster(`chalk-pointer`);
    const initialDirection = firstGuideLine.isReversed;
    const guideLineLength = firstGuideLine.length;
    let x = 0;
    let y = 0;
    const { width: canvasWidth } = this._canvas.getBoundingClientRect();
    const textureRadius = Math.floor((11 / 100) * canvasWidth);

    chalkPointer.opacity = 0;

    chalkPointer.onLoad = () => {
      chalkPointer.setWidth(textureRadius + 72);
      chalkPointer.setHeight(textureRadius + 72);
      chalkPointer.scale(1);

      if (initialDirection) {
        ({ x, y } = firstGuideLine.getPointAt(guideLineLength));
      } else {
        ({ x, y } = firstGuideLine.getPointAt(0));
      }

      chalkPointer.position = this.getNextChalkPointerPosition(chalkPointer, x, y, false);

      chalkPointer.opacity = 0;
    };

    return chalkPointer;
  }

  calculateHelperAdjustments() {
    const {
      _canvas: { width, height }
    } = this;

    const x = width - (95 / 100) * width;
    const y = (12.5 / 100) * height;

    return { x, y };
  }

  setHelperSmiley(firstGuideLine, show = true) {
    const initialDirection = firstGuideLine.isReversed;
    const guideLineLength = firstGuideLine.length;
    const { width: canvasWidth } = this._canvas.getBoundingClientRect();
    let textureRadius = Math.floor((11 / 100) * canvasWidth * 0.8);

    textureRadius = textureRadius > 90 ? textureRadius : 90;

    const helperAdjustments = this.calculateHelperAdjustments();

    let { x, y } = initialDirection
      ? firstGuideLine.getPointAt(guideLineLength)
      : firstGuideLine.getPointAt(0);

    x -= helperAdjustments.x + textureRadius / 4;
    y -= helperAdjustments.y - textureRadius / 4;

    this.setState(state => ({
      ...state,
      helpers: {
        ...state.helpers,
        smiley: {
          show,
          x,
          y,
          width: textureRadius,
          height: textureRadius
        }
      }
    }));

    this.setChalkPointOpacity(false, true);
  }

  setHelperArrow(firstGuideLine, show = false) {
    const initialDirection = firstGuideLine.isReversed;
    const guideLineLength = firstGuideLine.length;
    const { width: canvasWidth } = this._canvas.getBoundingClientRect();
    let textureRadius = Math.floor((11 / 100) * canvasWidth * 0.8);

    textureRadius = textureRadius > 90 ? textureRadius : 90;

    const helperAdjustments = this.calculateHelperAdjustments();

    let { x, y } = initialDirection
      ? firstGuideLine.getPointAt(guideLineLength)
      : firstGuideLine.getPointAt(0);

    x -= helperAdjustments.x + textureRadius / 4;
    y -= helperAdjustments.y - textureRadius / 4 - 20;

    this.setState(state => ({
      ...state,
      helpers: {
        ...state.helpers,
        arrow: {
          show,
          x,
          y,
          width: textureRadius,
          height: textureRadius
        }
      }
    }));

    this.setChalkPointOpacity(false, true);
  }

  moveChalkPointer(chalkPointer, x, y) {
    chalkPointer.position = this.getNextChalkPointerPosition(chalkPointer, x, y);
    this.props.handleAutoScroll(chalkPointer.position.y);
  }

  getNextChalkPointerPosition(chalkPointer, currentX, currentY) {
    return {
      x: currentX - chalkPointer.bounds.width / 4 + 95,
      y: currentY + chalkPointer.bounds.height / 4 + 40
    };
  }

  getPointerLabel() {
    const {
      props: { character },
      guideLineIndex
    } = this;

    const idx = guideLineIndex > 1 ? 1 : guideLineIndex; // it jumps from 0 to 2, so i had to improvise
    const number = character.split('')[idx];

    return `${t('pointerInstructionLabel')} ${number}.`;
  }

  renderHelpers() {
    const {
      state: {
        helpers: { arrow, smiley }
      }
    } = this;

    return (
      <>
        {arrow.show && (
          <button
            className="crayon-stroke-btn-overlay"
            role="button"
            aria-label={this.getPointerLabel()}
            tabIndex={1}
            onClick={this.handleStartIntro}
            style={{ top: arrow.y, left: arrow.x, width: arrow.width, height: arrow.height }}
          >
            <img id="arrow-pointer2" src={arrowPointerPath} />
          </button>
        )}
        {smiley.show && (
          <button
            className="crayon-stroke-btn-overlay"
            role="button"
            aria-label={this.getPointerLabel()}
            tabIndex={1}
            onClick={this.handleStartIntro}
            style={{ top: smiley.y, left: smiley.x, width: smiley.width, height: smiley.height }}
          >
            <img id="smiley-pointer2" src={smileyPointerPath} />
          </button>
        )}
      </>
    );
  }

  setSmiley = ({ show, x, y, height = 90, width = 90 }) =>
    this.setState(state => ({
      ...state,
      helpers: {
        ...state.helpers,
        smiley: {
          ...state.smiley,
          show,
          ...(x && { x }),
          ...(y && { y }),
          width,
          height
        }
      }
    }));

  setArrow = ({ show, x, y, height = 90, width = 90 }) =>
    this.setState(state => ({
      ...state,
      helpers: {
        ...state.helpers,
        arrow: {
          ...state.arrow,
          show,
          ...(x && { x }),
          ...(y && { y }),
          width,
          height
        }
      }
    }));

  render() {
    return (
      <div className="draw-activity-stage" ref={divEl => (this._container = divEl)}>
        <canvas
          ref={canvasEl => (this._canvas = canvasEl)}
          id="draw-activity"
          aria-label={`${t('Draw')} ${this.props.svgDescription}`}
        />
        <div className="draw-instruments">
          <img id="chalk-texture" src={chalkTexturePath} alt={t('CrayonTexture')} />
          <img id="chalk-pointer" src={chalkPointerPath} alt={t('CrayonPointer')} />
        </div>
        {this.renderHelpers()}
      </div>
    );
  }
}

DrawActivityTutorial.propTypes = {
  sndWrong: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  isMobile: PropTypes.bool,
  level: PropTypes.number,
  character: PropTypes.string,
  currentRound: PropTypes.shape({
    activityType: PropTypes.string,
    audio: PropTypes.array,
    data: PropTypes.shape({
      instructions: PropTypes.shape({
        start: PropTypes.string
      }),
      crayonDrawing: PropTypes.object
    })
  }),
  inactivityManager: PropTypes.object,
  logError: PropTypes.func,
  onRoundEnd: PropTypes.func,
  svgSounds: PropTypes.object,
  onComplete: PropTypes.func,
  onNextInstruction: PropTypes.func,
  segments: PropTypes.array,
  svgSrc: PropTypes.string,
  svgDescription: PropTypes.string,
  handleAutoScroll: PropTypes.func,
  helpers: PropTypes.object
};

const mapStateToProps = state => ({
  character: state.activity.data.character.character,
  sndWrong: state.ui.sounds.common.wrong || { play: () => {} },
  isMobile: state.ui.isMobile,
  helpers: state.activity.helpers
});

export default connect(mapStateToProps)(DrawActivityTutorial);
